/**
 * File: agentJobsStore.ts
 * Description: localStorage-backed store of tracked agent-job descriptors.
 *   Uses localStorage (not sessionStorage) so an in-flight agent run survives
 *   a full page reload and is visible across tabs. All access is wrapped so a
 *   missing/over-quota/private-mode storage degrades silently.
 */

import { AgentJobDescriptor, isTerminalStatus } from '../types/agentJob';

/** Map of jobId -> descriptor, the persisted shape. */
export type AgentJobMap = Record<string, AgentJobDescriptor>;

const STORAGE_KEY = 'archie.agentJobs.v1';

/**
 * Load every persisted descriptor. Returns an empty map when storage is
 * unavailable or the payload is missing/corrupt.
 */
export const loadAll = (): AgentJobMap => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) { return {}; }
    const parsed: unknown = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object') { return {}; }
    return parsed as AgentJobMap;
  } catch {
    return {};
  }
};

const writeAll = (jobs: AgentJobMap): void => {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(jobs));
  } catch {
    // Storage may be unavailable (private mode / quota). Degrade silently —
    // the in-memory tracker still works for the current session.
  }
};

/** Insert or replace a descriptor by its job id. */
export const upsert = (descriptor: AgentJobDescriptor): void => {
  const jobs = loadAll();
  jobs[descriptor.jobId] = descriptor;
  writeAll(jobs);
};

/** Remove a descriptor by job id (no-op when absent). */
export const remove = (jobId: string): void => {
  const jobs = loadAll();
  if (jobs[jobId]) {
    delete jobs[jobId];
    writeAll(jobs);
  }
};

/**
 * Drop terminal (succeeded/failed) descriptors that finished more than
 * `maxAgeMs` ago, so completed jobs are retained just long enough for a page
 * returning shortly after to read the cached result.
 *
 * @param maxAgeMs - Retention window for terminal jobs.
 * @param now - Current epoch ms (injectable for tests).
 */
export const pruneTerminalOlderThan = (
  maxAgeMs: number,
  now: number = Date.now(),
): void => {
  const jobs = loadAll();
  let changed = false;
  for (const [jobId, job] of Object.entries(jobs)) {
    if (
      isTerminalStatus(job.status) &&
      typeof job.finishedAt === 'number' &&
      now - job.finishedAt > maxAgeMs
    ) {
      delete jobs[jobId];
      changed = true;
    }
  }
  if (changed) { writeAll(jobs); }
};
